import type { Auth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js'

import type { LoginAttemptResult } from '@/helpers/AuthExternal'
import { configCatRequest, flags } from '@/helpers/ConfigCat'
import { authenticationMethod, environment } from '@/helpers/Environment'
import { withSellerPrefix } from '@/helpers/route'

import { apiErrorMessageOrReturn } from '@/errors/helpers'
import { loginAndTransferCart } from '@/state/Cart'

const loginPath = '/auth0-login'

function isAuth0(): boolean {
  return authenticationMethod === 'auth0_ecomm' && navigator.cookieEnabled
}

let auth0Client: Auth0Client | undefined = undefined

async function getAuth0ClientOptions(): Promise<Auth0ClientOptions | undefined> {
  const options = environment.web.auth0_login

  if (!options) return

  await configCatRequest

  const clientOptions: Auth0ClientOptions = {
    domain: options.domain,
    audience: options.audience,
    client_id: options.clientId,
    redirect_uri: window.location.origin + loginPath,
    // @see https://ticketure-team.atlassian.net/browse/TIC-6544
    ...(flags?.auth0_session_revocation && {
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
    }),
  }

  return clientOptions
}

async function getAuth0Client(): Promise<Auth0Client | undefined> {
  if (auth0Client) return auth0Client

  const options = await getAuth0ClientOptions()

  if (options && navigator.cookieEnabled) {
    // Lazy load auth0. Most tenants don't use it, but it makes up ~10% of the vendor bundle.
    const { default: createAuth0Client } = await importAuth0Sdk()

    // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
    auth0Client = await createAuth0Client(options)

    return auth0Client
  }
}

async function importAuth0Sdk(): Promise<typeof import('@auth0/auth0-spa-js')> {
  if (window.Cypress && window.__mockAuth0Import) {
    return window.__mockAuth0Import()
  }
  return await import('@auth0/auth0-spa-js')
}

async function isSessionRevoked(): Promise<boolean> {
  try {
    const client = await getAuth0Client()
    if (!client) return false

    // createAuth0Client() runs checkSession() → getTokenSilently({ ignoreCache: false }), which can return a cached access token without contacting Auth0. Force a network check so a revoked refresh token is detected.
    await client.getTokenSilently({ ignoreCache: true })
    return false
  } catch {
    // cleanup the cache (cookies and local storage)

    let client = auth0Client

    if (!client) {
      // create a temp client just so we can gracefully cleanup the cache
      const options = await getAuth0ClientOptions()

      if (options && navigator.cookieEnabled) {
        const { Auth0Client } = await importAuth0Sdk()
        client = new Auth0Client({
          ...options,
          useRefreshTokens: true,
          cacheLocation: 'localstorage',
        })
      }
    }

    client?.logout({ localOnly: true })

    return true
  }
}

async function login(currentPath: string): Promise<LoginAttemptResult> {
  const client = await getAuth0Client()
  if (!client) return { success: false, error: Error('Auth0 client not initialized') }

  // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
  try {
    await client.loginWithRedirect({
      redirect_uri: window.location.origin + loginPath,
      appState: { fullPath: withSellerPrefix(currentPath) },
    })

    return { success: false, error: Error('Auth0 redirect failed') }
  } catch (e) {
    return { success: false, error: e as Error }
  }
}

async function loginToTicketure(): Promise<LoginAttemptResult> {
  try {
    const client = await getAuth0Client()
    const claims = await client?.getIdTokenClaims()
    if (!claims) return { success: false, error: Error('Auth0 token not present') }

    await loginAndTransferCart({ jwt: claims.__raw })
    return { success: true }
  } catch (e) {
    return { success: false, error: e as Error }
  }
}

async function handleRedirectCallback(): Promise<LoginAttemptResult> {
  if (!isAuth0()) return { success: false, error: Error('Auth0 is not enabled') }

  const client = await getAuth0Client()

  if (!client) {
    return {
      success: false,
      error: Error('Auth0 could not be initialized'),
    }
  }

  try {
    // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
    const { appState } = await client.handleRedirectCallback()

    const { success, error } = await loginToTicketure()

    if (success) {
      // Navigate to the destination using window.location instead of Vue Router so that the router re-initializes using the seller prefix. See https://tixtrackteam.slack.com/archives/C02CMPTFLKF/p1748219412509819?thread_ts=1748212393.729409&cid=C02CMPTFLKF
      window.location = appState.fullPath
      return { success: true }
    }

    if (error) throw error

    return { success: false, error: Error('Login failed') }
  } catch (e: unknown) {
    if (e instanceof Error && e.message === 'Invalid state') {
      // User probably navigated here using browser forward/back button.
      window.history.forward()

      // Log the error if the browser is still on this page after 5 seconds.
      // That probably means there is no location in the history API to navigate forward to.
      setTimeout(() => {
        throw e
      }, 5000)

      return { success: false, error: e }
    } else {
      const { error } = apiErrorMessageOrReturn(e)

      return { success: false, error: error as Error }
    }
  }
}

async function logout(returnTo: string): Promise<LoginAttemptResult> {
  const client = await getAuth0Client()
  if (!client) return { success: false, error: Error('Auth0 client not initialized') }

  // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
  client.logout({ returnTo })

  return { success: true }
}

export const auth0 = {
  isAuth0,
  login,
  handleRedirectCallback,
  logout,
  loginPath,
  isSessionRevoked,
}
